import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem,
    CircularProgress, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Checkbox, Chip, FormControl, InputLabel,
    Select, Divider
} from '@mui/material';
import { Print, Visibility } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import api from '../services/api';

const ID_CARD_FORMATS = [
    { value: 'portrait', label: 'Portrait', description: 'Standard portrait' },
    { value: 'portrait1', label: 'Portrait 1', description: 'With department' },
    { value: 'landscape', label: 'Landscape', description: 'Horizontal' },
    { value: 'landscape1', label: 'Landscape 1', description: 'With photo left' }
];

const PrintStaffIdCard = () => {
    const [staff, setStaff] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [selectedFormat, setSelectedFormat] = useState('portrait');
    const [loading, setLoading] = useState(false);
    const [idCardData, setIdCardData] = useState(null);
    const [filters, setFilters] = useState({
        department: '',
        designation: '',
        status: 'active'
    });

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        pageStyle: `
            @page { 
                size: ${selectedFormat.includes('landscape') ? 'landscape' : 'portrait'}; 
                margin: 10mm;
            }
            @media print {
                body { -webkit-print-color-adjust: exact; }
            }
        `
    });

    useEffect(() => {
        fetchStaff();
    }, [filters]);

    const fetchStaff = async () => {
        setLoading(true);
        try {
            const res = await api.get('/staff', { params: filters });
            const formatted = (res.data.data || []).map(s => ({
                id: s.id,
                empId: s.employeeId || s.id,
                name: `${s.user?.firstName || s.firstName || ''} ${s.user?.lastName || s.lastName || ''}`.trim(),
                designation: s.designation,
                department: s.department,
                phone: s.phone || s.user?.phone,
                email: s.email || s.user?.email,
                joiningDate: s.joiningDate,
                photo: s.user?.profileImage || s.photo,
                bloodGroup: s.bloodGroup,
                address: s.address,
                gender: s.user?.gender || s.gender
            }));
            setStaff(formatted);
        } catch (error) {
            toast.error('Failed to fetch staff');
            setStaff([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedStaff(staff.map(s => s.id));
        } else {
            setSelectedStaff([]);
        }
    };

    const handleSelectStaff = (id) => {
        setSelectedStaff(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const handleGenerateCards = () => {
        if (selectedStaff.length === 0) {
            toast.warning('Please select at least one staff member');
            return;
        }
        const selected = staff.filter(s => selectedStaff.includes(s.id));
        setIdCardData(selected.map(s => ({ type: 'staff', format: selectedFormat, data: s })));
        toast.success(`Generated ${selected.length} ID cards`);
    };

    const renderIdCard = (staffMember, index) => {
        const isLandscape = selectedFormat.includes('landscape');
        const data = staffMember.data || staffMember;

        const cardStyle = {
            width: isLandscape ? 337 : 214,
            height: isLandscape ? 214 : 337,
            border: '2px solid #2e7d32',
            borderRadius: 8,
            background: 'linear-gradient(180deg, #2e7d32 0%, #4caf50 30%, #fff 30%)',
            overflow: 'hidden',
            pageBreakAfter: (index + 1) % 4 === 0 ? 'always' : 'auto',
            display: 'inline-block',
            margin: 8,
            position: 'relative'
        };

        return (
            <Box key={data.id || index} sx={cardStyle}>
                {/* Header */}
                <Box sx={{
                    background: 'linear-gradient(180deg, #2e7d32, #4caf50)',
                    color: 'white',
                    p: 1,
                    textAlign: 'center',
                    height: isLandscape ? 50 : 70
                }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: 10 }}>
                        VIDYA PRATISHTHAN'S
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: 11 }}>
                        SUPE ENGLISH MEDIUM SCHOOL
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 8 }}>STAFF IDENTITY CARD</Typography>
                </Box>

                {/* Body */}
                <Box sx={{ p: 1, display: 'flex', flexDirection: isLandscape ? 'row' : 'column', gap: 1 }}>
                    {/* Photo */}
                    <Box sx={{
                        width: isLandscape ? 60 : 80,
                        height: isLandscape ? 70 : 90,
                        border: '1px solid #2e7d32',
                        borderRadius: 1,
                        background: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: isLandscape ? 0 : 'auto',
                        mt: isLandscape ? 0 : 1
                    }}>
                        {data.photo ? (
                            <img src={data.photo} alt="Staff" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                        ) : (
                            <Typography variant="caption" color="text.secondary">Photo</Typography>
                        )}
                    </Box>

                    {/* Details */}
                    <Box sx={{ flex: 1, fontSize: 9 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 12, color: '#2e7d32', mb: 0.5 }}>
                            {data.name}
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 0.3 }}>
                            <Typography variant="caption"><b>Emp ID:</b> {data.empId}</Typography>
                            <Typography variant="caption"><b>Designation:</b> {data.designation || '-'}</Typography>
                            <Typography variant="caption"><b>Department:</b> {data.department || '-'}</Typography>
                            <Typography variant="caption"><b>Phone:</b> {data.phone || '-'}</Typography>
                            <Typography variant="caption"><b>Blood Group:</b> {data.bloodGroup || '-'}</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{
                    background: '#2e7d32',
                    color: 'white',
                    p: 0.5,
                    textAlign: 'center',
                    position: 'absolute',
                    bottom: 0,
                    width: '100%'
                }}>
                    <Typography variant="caption" sx={{ fontSize: 8 }}>
                        Valid for Academic Year 2025-2026
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Print Staff ID Cards</Typography>

            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" label="Department"
                            value={filters.department}
                            onChange={e => setFilters({ ...filters, department: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth size="small" label="Designation"
                            value={filters.designation}
                            onChange={e => setFilters({ ...filters, designation: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={e => setFilters({ ...filters, status: e.target.value })}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                                <MenuItem value="">All</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button variant="contained" onClick={fetchStaff} disabled={loading}>
                            {loading ? <CircularProgress size={20} /> : 'Search'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Format Selection */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Select ID Card Format:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ID_CARD_FORMATS.map(fmt => (
                        <Chip
                            key={fmt.value}
                            label={fmt.label}
                            onClick={() => setSelectedFormat(fmt.value)}
                            color={selectedFormat === fmt.value ? 'success' : 'default'}
                            variant={selectedFormat === fmt.value ? 'filled' : 'outlined'}
                        />
                    ))}
                </Box>
            </Paper>

            {/* Staff Selection Table */}
            {staff.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2">
                            Select Staff ({selectedStaff.length} of {staff.length} selected)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Visibility />}
                                onClick={handleGenerateCards}
                                disabled={selectedStaff.length === 0}
                            >
                                Generate Preview
                            </Button>
                            {idCardData && (
                                <Button
                                    variant="contained"
                                    startIcon={<Print />}
                                    onClick={handlePrint}
                                >
                                    Print Cards
                                </Button>
                            )}
                        </Box>
                    </Box>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedStaff.length === staff.length && staff.length > 0}
                                            indeterminate={selectedStaff.length > 0 && selectedStaff.length < staff.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell>Emp ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Designation</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Phone</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {staff.map(s => (
                                    <TableRow
                                        key={s.id}
                                        hover
                                        selected={selectedStaff.includes(s.id)}
                                        onClick={() => handleSelectStaff(s.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedStaff.includes(s.id)} />
                                        </TableCell>
                                        <TableCell>{s.empId}</TableCell>
                                        <TableCell>{s.name}</TableCell>
                                        <TableCell>{s.designation}</TableCell>
                                        <TableCell>{s.department}</TableCell>
                                        <TableCell>{s.phone}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* ID Card Preview */}
            {idCardData && (
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2">Staff ID Card Preview - {selectedFormat.toUpperCase()}</Typography>
                        <Button variant="contained" color="success" startIcon={<Print />} onClick={handlePrint}>
                            Print All ({idCardData.length} cards)
                        </Button>
                    </Box>
                    <Box ref={componentRef} sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'center',
                        p: 2,
                        background: '#f5f5f5',
                        minHeight: 400
                    }}>
                        {idCardData.map((card, idx) => renderIdCard(card, idx))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default PrintStaffIdCard;
